import axios from 'axios';

const ACCESS_TOKEN_KEY = 'token';
const USER_KEY = 'user';
const LAST_ACTIVITY_KEY = 'lastActivity';
let refreshPromise = null;
let interceptorInitialized = false;
let inMemoryAccessToken = '';
let hydratePromise = null;
const ADMIN_PATH_PREFIX = '/admin';

function shouldHydrateFromRefresh() {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith(ADMIN_PATH_PREFIX);
}

export function getAccessToken() {
  return inMemoryAccessToken || '';
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

export function setSession({ accessToken, user }) {
  if (accessToken) {
    inMemoryAccessToken = String(accessToken);
  }
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

export function clearSession() {
  inMemoryAccessToken = '';
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

async function requestTokenRefresh() {
  const response = await axios.post('/api/auth/refresh');
  const nextAccessToken = response.data?.accessToken || response.data?.token || '';
  const user = response.data?.user || null;

  if (!nextAccessToken) {
    throw new Error('Refresh response missing new access token');
  }

  setSession({ accessToken: nextAccessToken, user });
  return nextAccessToken;
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = requestTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function hydrateSessionFromRefresh() {
  if (getAccessToken()) {
    return true;
  }

  // Skip refresh bootstrap on public routes to avoid expected 400 noise when
  // no refresh cookie is present.
  if (!shouldHydrateFromRefresh()) {
    return false;
  }

  if (!hydratePromise) {
    hydratePromise = refreshAccessToken()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        hydratePromise = null;
      });
  }

  return hydratePromise;
}

export async function logoutSession({ redirect = true } = {}) {
  const accessToken = getAccessToken();
  try {
    const config = accessToken
      ? {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      : undefined;
    await axios.post('/api/auth/logout', null, config);
  } catch (_error) {
    // Ignore logout API failures and clear local session anyway.
  }

  clearSession();
  if (redirect && window.location.pathname.startsWith('/admin')) {
    window.location.assign('/admin/login');
  }
}

export function initializeAuthSession() {
  if (interceptorInitialized) return;
  interceptorInitialized = true;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (!token) return config;

    const nextConfig = { ...config };
    nextConfig.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`
    };
    return nextConfig;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response?.status;
      const requestUrl = String(originalRequest.url || '');

      const isAuthEndpoint =
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/refresh') ||
        requestUrl.includes('/api/auth/logout');

      if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
        return Promise.reject(error);
      }

      try {
        originalRequest._retry = true;
        const nextAccessToken = await refreshAccessToken();
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${nextAccessToken}`
        };
        return axios(originalRequest);
      } catch (refreshError) {
        await logoutSession({ redirect: true });
        return Promise.reject(refreshError);
      }
    }
  );
}
